/**
 * PDF generation for the FCM Family Philanthropy Purpose Questionnaire.
 *
 * Builds the document programmatically off the `show_statement.json`
 * response — no DOM scraping, no `window.print()`. Two modes:
 *
 *   - 'client'  → cover + purpose statement
 *   - 'advisor' → cover + statement + full Q&A summary (responses[])
 *
 * Layout (both modes):
 *   • Page 1: cover (plum band, title, prepared-for/by, date)
 *   • Page 2+: running header (title + "Forum Financial") and footer
 *     (Confidential + page X / Y) on every page after the cover
 */

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type {
  Content,
  TDocumentDefinitions,
} from 'pdfmake/interfaces';

// pdfmake ships its Roboto font as a base64 vfs blob. Different bundle
// versions expose it at different paths, so handle both.
(pdfMake as any).vfs =
  (pdfFonts as any)?.pdfMake?.vfs ?? (pdfFonts as any)?.vfs ?? (pdfFonts as any);

export type FcmPdfMode = 'client' | 'advisor';

// ───────────────────────── Forum brand ─────────────────────────
const COLORS = {
  plum: '#954982',
  plumDark: '#6b3460',
  plumLight: '#f3e8f1',
  teal: '#00BBE6',
  tealDark: '#0097b8',
  darkNavy: '#241A49',
  text: '#1a1a2e',
  textLight: '#6a6a7d',
  grayMid: '#9a9aaa',
  grayLine: '#e0e0ea',
};

// Letter @ 72dpi → 612 × 792. Side margins 50 → content width 512.
const PAGE_MARGINS: [number, number, number, number] = [50, 64, 50, 56];
const CONTENT_WIDTH = 512;

/**
 * Builds and downloads the FCM PDF. Caller passes the raw
 * `show_statement.json` response (`statementData`).
 */
export function downloadFcmPdf(mode: FcmPdfMode, statementData: any): void {
  const def = buildDocDefinition(mode, statementData);
  const submissionId = statementData?.submission?.id ?? 'submission';
  const fileName = `fcm-${mode}-${submissionId}.pdf`;
  pdfMake.createPdf(def).download(fileName);
}

// ───────────────────────── doc definition ─────────────────────────

function buildDocDefinition(mode: FcmPdfMode, data: any): TDocumentDefinitions {
  const submission = data?.submission ?? {};
  const user = data?.user ?? {};
  const responses: any[] = Array.isArray(data?.responses) ? data.responses : [];

  const statementText: string =
    submission.merged_statement || submission.purpose_statement || '';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  const today = formatLongDate(new Date());

  const content: Content[] = [];

  // ── Cover page ─────────────────────────────────────────────────
  content.push(...buildCoverContent(fullName, today, mode));

  // ── Statement body ──────────────────────────────────────────────
  content.push({ text: 'PURPOSE STATEMENT', style: 'sectionEyebrow', margin: [0, 0, 0, 6] });
  content.push({
    canvas: [hRule(0.6, COLORS.grayLine)],
    margin: [0, 0, 0, 14],
  });
  if (statementText) {
    content.push(...statementMarkdownToPdfContent(statementText));
  } else {
    content.push({
      text:
        'Your answers have been saved. Your personalized giving purpose ' +
        'statement is being prepared and will be available shortly.',
      style: 'placeholder',
    });
  }

  // ── Advisor view (Q&A summary) ──────────────────────────────────
  if (mode === 'advisor' && responses.length > 0) {
    content.push({ text: '', pageBreak: 'after' });
    content.push({ text: 'RESPONSE SUMMARY', style: 'sectionEyebrow', margin: [0, 0, 0, 6] });
    content.push({
      canvas: [hRule(0.6, COLORS.grayLine)],
      margin: [0, 0, 0, 14],
    });
    content.push(...buildAdvisorResponseSections(responses));
  }

  return {
    info: {
      title: `FCM Statement — ${fullName || 'Client'}`,
      author: 'Forum Financial',
      subject: 'Family Philanthropy Purpose Statement',
    },
    pageSize: 'LETTER',
    pageMargins: PAGE_MARGINS,
    defaultStyle: { font: 'Roboto', fontSize: 10.5, color: COLORS.text, lineHeight: 1.45 },
    content,

    // Running header — suppressed on the cover (page 1)
    header: (currentPage: number) => {
      if (currentPage === 1) return null as any;
      return {
        columns: [
          { text: 'Family Philanthropy Purpose Statement', style: 'pageHeaderLeft' },
          { text: 'Forum Financial', style: 'pageHeaderRight', alignment: 'right' },
        ],
        margin: [50, 28, 50, 0],
      };
    },

    // Footer on every page
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        { text: 'Confidential — Prepared for client use', style: 'pageFooterLeft' },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          style: 'pageFooterRight',
          alignment: 'right',
        },
      ],
      margin: [50, 16, 50, 0],
    }),

    styles: {
      // Cover
      coverEyebrow: {
        fontSize: 9,
        bold: true,
        color: COLORS.plum,
        characterSpacing: 1.4,
        margin: [0, 0, 0, 4],
      },
      coverTitle1: { fontSize: 30, bold: true, color: COLORS.darkNavy, lineHeight: 1.1 },
      coverTitle2: { fontSize: 30, bold: true, color: COLORS.plum, lineHeight: 1.1 },
      coverPreparedFor: { fontSize: 11, color: COLORS.textLight, characterSpacing: 0.8, bold: true },
      coverName: { fontSize: 18, bold: true, color: COLORS.darkNavy, margin: [0, 4, 0, 0] },
      coverDate: { fontSize: 10, color: COLORS.textLight, italics: true, margin: [0, 12, 0, 0] },
      coverFooter: { fontSize: 9, color: COLORS.grayMid, characterSpacing: 0.8 },

      // Section headers
      sectionEyebrow: {
        fontSize: 10,
        bold: true,
        color: COLORS.plum,
        characterSpacing: 1.2,
      },
      h2: {
        fontSize: 14,
        bold: true,
        color: COLORS.darkNavy,
        characterSpacing: 0.6,
        margin: [0, 16, 0, 6],
      },

      // Statement body
      paragraph: { fontSize: 10.5, margin: [0, 0, 0, 8], color: COLORS.text },
      bullet: { fontSize: 10.5, color: COLORS.text },
      placeholder: { fontSize: 11, italics: true, color: COLORS.textLight },

      // Advisor closer
      advisorCloserEyebrow: {
        fontSize: 9,
        bold: true,
        color: COLORS.grayMid,
        characterSpacing: 1.2,
        margin: [0, 0, 0, 6],
      },

      // Q&A summary (advisor mode)
      advisorSectionTitle: {
        fontSize: 11,
        bold: true,
        color: COLORS.plum,
        characterSpacing: 1.0,
        margin: [0, 14, 0, 8],
      },
      advisorQ: { fontSize: 8.5, color: COLORS.textLight, bold: true, characterSpacing: 0.4 },
      advisorA: { fontSize: 10, color: COLORS.text, margin: [0, 1, 0, 6] },

      // Running header / footer
      pageHeaderLeft: { fontSize: 8.5, color: COLORS.textLight, characterSpacing: 0.4 },
      pageHeaderRight: { fontSize: 8.5, color: COLORS.plum, bold: true, characterSpacing: 0.6 },
      pageFooterLeft: { fontSize: 8, color: COLORS.grayMid, italics: true },
      pageFooterRight: { fontSize: 8, color: COLORS.grayMid },
    },
  };
}

// ───────────────────────── cover page ─────────────────────────

function buildCoverContent(
  fullName: string,
  dateStr: string,
  mode: FcmPdfMode,
): Content[] {
  return [
    // Plum band across the top (drawn with negative x so it extends to edges)
    {
      canvas: [
        { type: 'rect', x: -50, y: -10, w: 612, h: 70, color: COLORS.plum },
      ],
      margin: [0, 0, 0, 0],
    },
    // Eyebrow + title
    { text: 'FORUM FINANCIAL', style: 'coverEyebrow', margin: [0, 60, 0, 24] },
    { text: 'Family Philanthropy', style: 'coverTitle1' },
    { text: 'Purpose Statement', style: 'coverTitle2', margin: [0, 0, 0, 0] },
    // Decorative rule
    {
      canvas: [
        { type: 'line', x1: 0, y1: 0, x2: 80, y2: 0, lineWidth: 3, lineColor: COLORS.teal },
      ],
      margin: [0, 18, 0, 28],
    },
    // Prepared for
    { text: 'PREPARED FOR', style: 'coverPreparedFor' },
    { text: fullName || 'Client', style: 'coverName' },
    { text: dateStr, style: 'coverDate' },
    // Spacer pushing the rest to the bottom of the cover
    { text: '', margin: [0, 200, 0, 0] },
    // Bottom rule + footer
    {
      canvas: [hRule(0.6, COLORS.grayLine)],
      margin: [0, 0, 0, 10],
    },
    {
      columns: [
        { text: 'Prepared by Forum Financial', style: 'coverFooter' },
        {
          text: mode === 'advisor' ? 'ADVISOR COPY' : 'CLIENT COPY',
          style: 'coverFooter',
          alignment: 'right',
        },
      ],
    },
    { text: '', pageBreak: 'after' },
  ];
}

// ─────────────── markdown → pdfmake content nodes ───────────────

/**
 * Parses the BE's markdown statement into pdfmake content nodes.
 * Supports `## Heading`, `- bullet`, `**bold**`, `*italic*`, paragraphs,
 * and the special `## For Your Forum Advisor` treatment (divider + small
 * caps eyebrow, matching the on-screen rendering).
 */
function statementMarkdownToPdfContent(text: string): Content[] {
  const out: Content[] = [];
  const lines = text.split('\n');
  let bulletBuffer: Content[] = [];

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    out.push({ ul: bulletBuffer, margin: [0, 0, 0, 8] });
    bulletBuffer = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\r$/, '');
    const trimmed = line.trim();

    if (/^# [^#]/.test(trimmed)) {
      flushBullets();
      continue; // title already on cover
    }
    if (/^## (.+)/.test(trimmed)) {
      flushBullets();
      const heading = trimmed.replace(/^## /, '');
      if (heading === 'For Your Forum Advisor') {
        out.push({
          canvas: [hRule(0.6, COLORS.grayLine)],
          margin: [0, 18, 0, 10],
        });
        out.push({
          text: 'FOR YOUR FORUM ADVISOR',
          style: 'advisorCloserEyebrow',
        });
      } else {
        out.push({ text: heading.toUpperCase(), style: 'h2' });
      }
      continue;
    }
    if (/^---+$/.test(trimmed)) {
      flushBullets();
      out.push({
        canvas: [hRule(0.6, COLORS.grayLine)],
        margin: [0, 10, 0, 10],
      });
      continue;
    }
    if (/^- (.+)/.test(trimmed)) {
      bulletBuffer.push({
        text: inlineRunsFor(trimmed.replace(/^- /, '')) as any,
        style: 'bullet',
        margin: [0, 0, 0, 4],
      });
      continue;
    }
    if (trimmed === '') {
      flushBullets();
      continue;
    }
    flushBullets();
    out.push({ text: inlineRunsFor(line) as any, style: 'paragraph' });
  }
  flushBullets();
  return out;
}

/**
 * Converts inline `**bold**` and `*italic*` runs into pdfmake's text-array
 * form so the bold/italic spans render correctly.
 */
function inlineRunsFor(text: string): Content | string {
  type Run = { text: string; bold?: boolean; italics?: boolean };
  const runs: Run[] = [];
  const re = /\*\*(.+?)\*\*|\*([^*]+?)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) runs.push({ text: text.slice(last, m.index) });
    if (m[1] !== undefined) runs.push({ text: m[1], bold: true });
    else if (m[2] !== undefined) runs.push({ text: m[2], italics: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last) });
  if (runs.length === 1 && !runs[0].bold && !runs[0].italics) return runs[0].text;
  return runs as unknown as Content;
}

// ─────────────── advisor Q&A summary ───────────────

const SECTION_LABELS: Record<number, string> = {
  1: 'Section 1 — Origin & Motivation',
  2: 'Section 2 — Core Values',
  3: 'Section 3 — Focus & Priorities',
  4: 'Section 4 — Family & Legacy',
  5: 'Section 5 — Strategy & Style',
  6: 'Additional Notes',
};

function buildAdvisorResponseSections(responses: any[]): Content[] {
  // Group by section → question_code so multi-select rows merge.
  const bySection = new Map<number, Map<string, { label: string; values: string[] }>>();
  for (const r of responses) {
    const sec = Number(r.section);
    if (!bySection.has(sec)) bySection.set(sec, new Map());
    const qMap = bySection.get(sec)!;
    if (!qMap.has(r.question_code)) {
      qMap.set(r.question_code, { label: r.question_label, values: [] });
    }
    const display =
      r.text_value !== null && r.text_value !== undefined && r.text_value !== ''
        ? String(r.text_value)
        : r.answer_label || '';
    if (display) qMap.get(r.question_code)!.values.push(display);
  }

  const content: Content[] = [];
  const sortedSections = Array.from(bySection.keys()).sort((a, b) => a - b);

  for (const sec of sortedSections) {
    content.push({
      text: (SECTION_LABELS[sec] || `Section ${sec}`).toUpperCase(),
      style: 'advisorSectionTitle',
    });
    content.push({
      canvas: [hRule(0.5, COLORS.grayLine)],
      margin: [0, 0, 0, 6],
    });
    for (const q of bySection.get(sec)!.values()) {
      content.push({ text: q.label.toUpperCase(), style: 'advisorQ' });
      content.push({ text: q.values.join(', ') || '—', style: 'advisorA' });
    }
  }
  return content;
}

// ───────────────────────── helpers ─────────────────────────

function hRule(thickness: number, color: string) {
  return {
    type: 'line' as const,
    x1: 0,
    y1: 0,
    x2: CONTENT_WIDTH,
    y2: 0,
    lineWidth: thickness,
    lineColor: color,
  };
}

function formatLongDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
